export type MinimumBuildState =
  | 'CURRENT'
  | 'OUTDATED'
  | 'UNCONFIGURED'
  | 'OFFLINE_UNKNOWN'
  | 'SERVICE_WORKER_UNCERTAIN';

export interface MinimumBuildResult {
  state: MinimumBuildState;
  currentBuild: string | null;
  minimumBuild: string | null;
  checkedAt: number;
}

export interface MinimumBuildInput {
  currentBuild?: string;
  minimumBuild?: string;
  online: boolean;
  hasUnexpectedServiceWorkerController: boolean;
  now?: number;
}

let bootstrapResult: MinimumBuildResult = {
  state: 'UNCONFIGURED',
  currentBuild: null,
  minimumBuild: null,
  checkedAt: 0,
};

function parseBuild(value: string): number[] | null {
  const normalized = value.trim().replace(/^v/i, '').split(/[+-]/, 1)[0];
  if (!/^\d+(?:\.\d+)*$/.test(normalized)) return null;
  return normalized.split('.').map(part => Number(part));
}

function compareBuilds(left: string, right: string): number | null {
  const leftParts = parseBuild(left);
  const rightParts = parseBuild(right);
  if (!leftParts || !rightParts) return null;
  const length = Math.max(leftParts.length, rightParts.length);
  for (let index = 0; index < length; index += 1) {
    const difference = (leftParts[index] ?? 0) - (rightParts[index] ?? 0);
    if (difference !== 0) return difference;
  }
  return 0;
}

export function evaluateMinimumBuild(input: MinimumBuildInput): MinimumBuildResult {
  const currentBuild = input.currentBuild?.trim() || null;
  const minimumBuild = input.minimumBuild?.trim() || null;
  const checkedAt = input.now ?? Date.now();

  if (!input.online) {
    return { state: 'OFFLINE_UNKNOWN', currentBuild, minimumBuild, checkedAt };
  }
  if (input.hasUnexpectedServiceWorkerController) {
    return { state: 'SERVICE_WORKER_UNCERTAIN', currentBuild, minimumBuild, checkedAt };
  }
  if (!currentBuild || !minimumBuild) {
    return { state: 'UNCONFIGURED', currentBuild, minimumBuild, checkedAt };
  }
  const comparison = compareBuilds(currentBuild, minimumBuild);
  if (comparison === null) {
    return { state: 'UNCONFIGURED', currentBuild, minimumBuild, checkedAt };
  }
  return {
    state: comparison >= 0 ? 'CURRENT' : 'OUTDATED',
    currentBuild,
    minimumBuild,
    checkedAt,
  };
}

export function runMinimumBuildBootstrap(): MinimumBuildResult {
  bootstrapResult = evaluateMinimumBuild({
    currentBuild: import.meta.env.VITE_GENEALOGY_BUILD_ID,
    minimumBuild: import.meta.env.VITE_MINIMUM_GENEALOGY_BUILD_ID,
    online: typeof navigator !== 'undefined' && navigator.onLine,
    hasUnexpectedServiceWorkerController:
      typeof navigator !== 'undefined' &&
      'serviceWorker' in navigator &&
      navigator.serviceWorker.controller !== null,
  });
  return bootstrapResult;
}

export function getMinimumBuildResult(): MinimumBuildResult {
  return bootstrapResult;
}

export function setMinimumBuildResultForTests(result: MinimumBuildResult): void {
  bootstrapResult = result;
}
